/**
 * Module 2: your queue and your real backlog (3 minutes). The badge, the default view
 * (today, your mailboxes, one row per case), the backlog chips, the Action column, the
 * tabs with More and the Filters button.
 *
 * Counts match practice/facts.ts QUEUE_EXPECT: the default view shows 4 rows, all dates
 * shows 11 open, 4 of them older than 7 days. Round 3 (12 Oct 2026): the queue row
 * figure on q2; every sentence in Simplified Technical English.
 */

import type { Module } from "@/features/training/types";

import { checkSlide } from "../check-slide";

export const MODULE: Module = {
  id: "queue",
  title: "Your queue and your real backlog",
  menuTitle: "Queue",
  minutes: 3,
  kind: "lesson",
  goals: [
    "Read the counts next to `Queue` in the sidebar.",
    "Know that the default view is not your whole backlog.",
    "Find old cases with the backlog chips.",
    "Read the Action column before you open a case.",
  ],
  helpTopics: ["queue"],
  slides: [
    {
      id: "q1",
      tone: "light",
      title: "When you need to see how much work is waiting",
      job: "When you need to see how much work is waiting, open the queue.",
      path: ["Sidebar", "Queue"],
      body: [
        {
          kind: "text",
          text: "The sidebar shows two numbers next to `Queue`, for example `4 new · 11 open`. The first number is the count of the default view. The second number is **all open cases, all dates**.",
        },
        {
          kind: "text",
          text: "The queue shows one row per case: received, customer, subject, lines found, the one action word, and who has it. The backlog chips above the list count the open work over all dates.",
        },
      ],
      interaction: {
        id: "q1-parts",
        kind: "hotspots",
        required: true,
        minOpen: 3,
        stage: { view: "queue", preset: "default" },
        spots: [
          {
            anchor: "queue-badge",
            label: "The two numbers",
            text: "4 cases in the default view. 11 open cases over all dates. The second number is your real backlog.",
          },
          {
            anchor: "queue-backlog-chips",
            label: "Backlog chips",
            text: "Click a chip. It opens all open cases, the cases older than 7 days, or the cases that came back for you.",
          },
          {
            anchor: "queue-tabs",
            label: "Tabs",
            text: "`To review` is your main list. `Ready to quote` holds the cases that you can finish at once. `Closed` is filed work. Every other list sits under `More`.",
          },
        ],
      },
      check: "If the two numbers are far apart, open the backlog chips. Old cases are waiting.",
      labels: ["Queue", "To review", "Ready to quote", "Closed", "More"],
    },
    {
      id: "q2",
      tone: "grey",
      title: "The default view shows today",
      job: "Find the cases the default view does not show.",
      path: ["Sidebar", "Queue", "open older than 7 days"],
      body: [
        {
          kind: "text",
          text: "The default view shows the cases that came in **today**, in your mailboxes, in `To review`. Older cases and cases a colleague took are not in it.",
        },
        { kind: "figure", figure: "queue-row" },
        {
          kind: "text",
          text: "The backlog chips lead to older work: `open, all dates`, `open older than 7 days` and `Back for you`. Everything else about the list sits behind one button, `Filters`.",
        },
        {
          kind: "text",
          text: "On your first days, start with the tab `Ready to quote`: you can finish these cases at once. The customer is certain, and every line is matched and priced.",
        },
        {
          kind: "note",
          tone: "warning",
          title: "A short list is not an empty backlog",
          text: "In a test, one area saw 1 of 116 open cases in the default view. The backlog chips open the older inquiries. Check them every day.",
        },
      ],
      interaction: {
        id: "q2-backlog",
        kind: "practice",
        required: true,
        stage: { view: "queue", preset: "default" },
        task: "find-backlog",
        steps: [
          "Look at the list. It shows 4 cases.",
          "Click the chip `11 open, all dates`.",
          "Click the chip `open older than 7 days`.",
        ],
        success:
          "You found 7 open cases the default view did not show. 4 of them are older than 7 days.",
        hints: [
          "The chips sit above the list, under the page title.",
          "`Filters` opens the rest: who has it, mailbox, area, sender, dates.",
        ],
      },
      check: "Open `open older than 7 days` once a day. Old inquiries are often the most urgent.",
      labels: [
        "open, all dates",
        "open older than 7 days",
        "Back for you",
        "Filters",
        "To review",
        "Ready to quote",
      ],
    },
    {
      id: "q3",
      tone: "light",
      title: "Which list holds what",
      job: "Sort each list: does it still need you, or is it filed or parked?",
      body: [
        {
          kind: "text",
          text: "Three tabs are always there: `To review`, `Ready to quote` and `Closed`. The other lists sit under `More`. Most work is in `To review`. You can finish the cases in `Ready to quote` at once.",
        },
      ],
      interaction: {
        id: "q3-tabs",
        kind: "sort",
        required: true,
        prompt: "Put each list in the right group.",
        bins: ["Still needs you", "Filed or parked"],
        cards: [
          {
            id: "q3-review",
            text: "To review",
            bin: 0,
            why: "Your main list. Every inquiry with a proposal from I2Q starts here.",
          },
          {
            id: "q3-back",
            text: "Back for you",
            bin: 0,
            why: "A customer you confirmed wrote again, or a waiting case is due. Reply to these first.",
          },
          {
            id: "q3-customer",
            text: "Customer to confirm",
            bin: 0,
            why: "I2Q is not sure who the customer is. Prices come after you confirm the customer.",
          },
          {
            id: "q3-closed",
            text: "Closed",
            bin: 1,
            why: "Filed in I2Q only. The customer may still wait.",
          },
          {
            id: "q3-parked",
            text: "Purchase orders (parked)",
            bin: 1,
            why: "I2Q counts purchase orders but does not work on them. Handle them in F&O as usual.",
          },
          {
            id: "q3-waiting",
            text: "Waiting for customer",
            bin: 1,
            why: "You asked the customer something. The case comes back to you when it is due.",
          },
        ],
      },
      check: "Closed means filed, not replied. Look in the thread when you are not sure.",
      labels: [
        "To review",
        "Ready to quote",
        "Closed",
        "More",
        "Customer to confirm",
        "Waiting for customer",
        "Purchase orders (parked)",
        "Not an inquiry",
      ],
    },
    {
      id: "q4",
      tone: "grey",
      title: "Read the Action column",
      job: "When you need to pick the next case, read the Action column.",
      body: [
        {
          kind: "text",
          text: "The Action column says in one word what the case needs from you. The icon in front of the word is the same as on the case page. A person with a magnifier means Confirm. An orange box means Pick. A warning triangle means Check. The words, in the order you meet them:",
        },
        {
          kind: "list",
          items: [
            "`Reading`: I2Q has not finished with the mail. `Could not read`: handle it by hand.",
            "`Nothing to do`: closed, quoted, a copy of another mail, or not an inquiry.",
            "`Find customer` or `Confirm`: the customer first. Prices follow the customer.",
            "`Pick 2`: two rows need a confirm or a pick. `Check`: a rule holds the case.",
            "`Prepare reply` or `Copy reply`: the lines are done. Reply to the customer.",
          ],
        },
        {
          kind: "text",
          text: "The Lines column says how many rows have an item, for example `1/3 found`. It also says how many rows you still decide. The customer cell says `to confirm` or `not identified` under the name until the customer is sure. Without an account the name is the mail domain of the sender. A public mail address such as gmail.com names no company, so the name is then the sender.",
        },
      ],
      interaction: {
        id: "q4-why",
        kind: "hotspots",
        required: false,
        minOpen: 2,
        stage: { view: "queue", preset: "default" },
        spots: [
          {
            anchor: "queue-action",
            label: "Action",
            text: "The one word. Confirm means: the customer is a suggestion. Open the case and read the evidence first.",
          },
          {
            anchor: "queue-lines",
            label: "Lines",
            text: "1 of 3 rows has an item, 2 are still to decide. Open the case and check every row.",
          },
        ],
      },
      check: "The action word is the next step, not a verdict. Read the mail before you confirm.",
      labels: ["Find customer", "Confirm", "Check", "Prepare reply", "Copy reply", "Nothing to do"],
    },
    {
      id: "q5",
      tone: "light",
      title: "Check: the other lists",
      job: "Click the right option to finish the module.",
      interaction: {
        id: "q5-check",
        kind: "check",
        required: true,
        question: {
          id: "q5-q",
          topic: "queue",
          stem: "You need the list `Not an inquiry`. Where is it?",
          options: [
            {
              id: "q5-q-a",
              text: "Under `More`, next to the tabs.",
              correct: true,
              fb: "Right. `To review`, `Ready to quote` and `Closed` are always there. The other lists sit under `More`.",
            },
            {
              id: "q5-q-b",
              text: "In `Closed`.",
              correct: false,
              fb: "No. `Closed` holds the inquiries that are done, not for us, or closed by I2Q.",
            },
            {
              id: "q5-q-c",
              text: "Behind the chip `open, all dates`.",
              correct: false,
              fb: "No. The chips change the dates, not the list.",
            },
            {
              id: "q5-q-d",
              text: "Nowhere. I2Q deletes those mails.",
              correct: false,
              fb: "No. I2Q deletes nothing. It counts a mail that is not an inquiry and lists it under `More`.",
            },
          ],
        },
      },
      check: "Three tabs and `More`. The lists you rarely need are one click further.",
      labels: ["Not an inquiry", "More", "Closed", "To review", "Ready to quote"],
    },
    checkSlide({
      id: "q6",
      tone: "grey",
      title: "Check: the default view",
      topic: "queue",
      stem: "Which cases does the default view show?",
      answers: [
        ["Every open case.", false, "No. The default view shows only some of your backlog."],
        [
          "Today's cases of your mailboxes, yours and nobody's.",
          true,
          "Right. Older cases and colleagues' cases are not in it.",
        ],
        ["Only the cases you took.", false, "No. It shows your cases and the unassigned ones."],
        [
          "Every case of this week.",
          false,
          "No. Pick the dates under `Filters` for that, or use the backlog chips.",
        ],
      ],
      check: "A short default view is not an empty backlog.",
      labels: ["Filters"],
    }),
    checkSlide({
      id: "q7",
      tone: "light",
      title: "Check: the real backlog",
      topic: "queue",
      stem: "The sidebar says `4 new · 11 open`. How many open cases are there?",
      answers: [
        ["4", false, "No. 4 is what the default view shows."],
        ["15", false, "No. The 4 cases are inside the 11. The numbers do not add up."],
        ["11", true, "Right. The second number is all open cases, all dates."],
        ["It depends on the tab.", false, "No. The second number counts every open case."],
      ],
      check: "Read the second number. That is your real backlog.",
    }),
  ],
};
